<template>
  <v-container id="devices_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog"></router-view>

    <!-- CONTENT -->
    <v-row class="mb-6 px-6" align="center" justify="center">
      <v-col cols="7">
        <span class="text-h5">
          {{ $t('devices.title.list') }}
        </span>
      </v-col>

      <v-col class="d-flex flex-row align-self-end flexendforced" align="sretch">
        <v-btn class="align-self-end mr-8" color="primary" to="/devices/add">
          <v-icon left>mdi-plus-circle-outline</v-icon>
          <span class="ml-2 text-capitalize">{{ $t('devices.content.addDevice') }}</span>
        </v-btn>

        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          single-line
          hide-details
          clearable
          :label="$t('dataTable.search')"
        >
        </v-text-field>
      </v-col>
    </v-row>

    <v-card class="pa-5">
      <v-data-table
        id="devices_table"
        must-sort
        :headers="headers"
        :items="deviceList"
        :options.sync="pagination"
        :server-items-length="totalItems"
        :loading="dataTableLoading"
        :loading-text="$t('dataTable.content.loadingPleaseWait')"
        :no-data-text="$t('dataTable.content.noDataAvailable')"
        :footer-props="{
          'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
          'page-text': $t('dataTable.footer.pageText'),
          'items-per-page-options': [10,25,50]
        }"
      >
        <template #[`item.status`]="{item}">
          <v-chip label :color="getChipStyle(item)">
            {{ getChipText(item) }}
          </v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn id="manage" text class="pa-1 " :to="'/devices/' + item.id + '/update'">
            <v-icon>
              mdi-tune-variant
            </v-icon>
            {{ $t('dataTable.content.devices.manage') }}
          </v-btn>
          <v-btn id="delete" class="pa-1 " text @click="deleteDevice(item.id,$event)">
            <v-icon>
              mdi-delete
            </v-icon>
            {{ $t('dataTable.content.devices.delete') }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import Role from '../../Role'
import devices from '../../services/api/devices'
import debounce from 'lodash.debounce'
import CommonError from '../CommonError'
import { showToastSuccess, showToastError, showConfirmationMenu } from '../../event-bus'

export default {
  name: 'Devices',

  data() {
    return {
      search: null,
      searchBoxClosed: true,
      pagination: {
        sortBy: ['friendlyName']
      },
      deviceList: [],
      totalItems: 0,
      dataTableLoading: false,
      headers: [
        {
          text: this.$t('dataTable.header.devices.friendlyName'),
          align: 'left',
          value: 'friendlyName',
          class: 'text-uppercase'
        },
        {
          text: this.$t('dataTable.header.devices.status'),
          align: 'left',
          value: 'status',
          sortable: false,
          class: 'text-uppercase'
        },
        {
          text: this.$t('dataTable.header.createdAt'),
          align: 'left',
          value: 'createdAt',
          class: 'text-uppercase'
        },
        {
          text: this.$t('dataTable.header.devices.user'),
          align: 'left',
          value: 'user.name',
          class: 'text-uppercase'
        },
        {
          text: this.$t('dataTable.header.devices.email'),
          align: 'left',
          value: 'user.email',
          class: 'text-uppercase'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false,
          class: 'text-uppercase'
        }
      ]
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchDeviceWatcher()
    },

    pagination() {
      this.searchDevices()
    }
  },

  methods: {
    async searchDevices() {
      this.dataTableLoading = true

      const pageRequest = {
        'page-num': this.pagination.page,
        'page-size': this.pagination.itemsPerPage || 10,
        'sort-field': this.pagination.sortBy[0],
        'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
      }

      try {
        const page = this.isAdmin ? await devices.listDevices(null, this.search, pageRequest) : await devices.listMyDevices(this.search, pageRequest)
        this.deviceList = page.content
        this.totalItems = page.total
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.dataTableLoading = false
      }
    },

    searchDeviceWatcher: debounce(function() {
      this.searchDevices()
    }, 500),

    async deleteDevice(id, e) {
      const conf = await showConfirmationMenu({ title: this.$t('dialog.confirmation.title'), posX: e.clientX, posY: e.clientY })
      if (!conf) {
        return
      }

      try {
        await devices.deleteDevice(id)
        this.searchDevices()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if ((error.response !== undefined) && (error.response.data.error !== undefined)) {
          const splittedErrorMessage = error.response.data.error_description.split(' ')
          if (error.response.data.error === CommonError.STILL_REFERENCED) {
            errorMsg = this.$t('toastMessage.stillReferenced', { 0: splittedErrorMessage[0], 1: splittedErrorMessage[5] })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)
      }
    },

    getChipStyle(item) {
      if (item.enabled === true) {
        return 'enabled'
      } else {
        return 'inactive'
      }
    },

    getChipText(item) {
      if (item.enabled === true) {
        return this.$t('dataTable.header.devices.states.enabled')
      } else {
        return this.$t('dataTable.header.devices.states.inactive')
      }
    }
  }
}
</script>

<style>
/* These are not scoped because they are needed in ./SignatureChainPaticipants.vue */
.enabled {
  background-color: #e7ffd9 !important;
  color: #64c636 !important;
}
.inactive {
  background-color: #d1e8ff !important;
  color: #007afe !important;
}
</style>
